import { useState, useEffect, useMemo, useRef } from "react";
import AdminLayout from "../../components/AdminLayout";
import { toast } from "sonner";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Plus,
  Edit2,
  Trash2,
  Search,
  Music,
  Disc,
  Youtube,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  LayoutGrid,
  List as ListIcon,
  X as XIcon,
  Play,
  Pause,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

export default function AdminMusics() {
  const [view, setView] = useState("albums"); // "albums" | "list"
  const [playlists, setPlaylists] = useState([]);
  const [musics, setMusics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMusic, setEditingMusic] = useState(null);
  const [contextPlaylistId, setContextPlaylistId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null); // {title, description, confirmLabel, danger, onConfirm}
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);
  const [form, setForm] = useState({
    title: "",
    artist_name: "",
    youtube_id: "",
    spotify_id: "",
    duration: "",
    release_date: "",
    is_release: false,
    plays: 0,
  });

  const reload = async () => {
    setLoading(true);
    try {
      const [musicsRes, plListRes] = await Promise.all([
        axios.get(`${API}/musics?limit=300`, { headers: headers() }),
        axios.get(`${API}/playlists`),
      ]);
      setMusics(musicsRes.data || []);
      // Fetch each playlist with its musics
      const pls = await Promise.all(
        (plListRes.data || []).map(async (p) => {
          try {
            const full = await axios.get(`${API}/playlists/${p.id}`);
            return full.data;
          } catch {
            return { ...p, musics: [] };
          }
        })
      );
      setPlaylists(pls);
    } catch (err) {
      console.error(err);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const resetForm = () => {
    setEditingMusic(null);
    setContextPlaylistId(null);
    setForm({
      title: "",
      artist_name: "",
      youtube_id: "",
      spotify_id: "",
      duration: "",
      release_date: "",
      is_release: false,
      plays: 0,
    });
  };

  const openNewMusic = (playlistId = null, defaultArtist = "") => {
    resetForm();
    setContextPlaylistId(playlistId);
    setForm((prev) => ({ ...prev, artist_name: defaultArtist }));
    setDialogOpen(true);
  };

  const handleEdit = (music, playlistId = null) => {
    setEditingMusic(music);
    setContextPlaylistId(playlistId);
    setForm({
      title: music.title || "",
      artist_name: music.artist_name || "",
      youtube_id: music.youtube_id || "",
      spotify_id: music.spotify_id || "",
      duration: music.duration || "",
      release_date: music.release_date || "",
      is_release: music.is_release || false,
      plays: music.plays || 0,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.title || !form.artist_name) {
      toast.error("Título e artista são obrigatórios");
      return;
    }
    try {
      let newMusicId = editingMusic?.id;
      if (editingMusic) {
        // True update: preserves the same ID and all playlist links
        await axios.put(`${API}/musics/${editingMusic.id}`, form, { headers: headers() });
      } else {
        const res = await axios.post(`${API}/musics`, form, { headers: headers() });
        newMusicId = res.data?.id || newMusicId;
      }

      // Auto-link to playlist if context was set (only for NEW musics)
      if (!editingMusic && contextPlaylistId && newMusicId) {
        try {
          await axios.post(
            `${API}/playlists/${contextPlaylistId}/musics/${newMusicId}`,
            {},
            { headers: headers() }
          );
        } catch (e) {
          console.warn("Linking music to playlist failed", e);
        }
      }

      toast.success(editingMusic ? "Música atualizada!" : "Música adicionada!");
      setDialogOpen(false);
      resetForm();
      reload();
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail || "";
      toast.error(detail ? `Erro: ${detail}` : "Erro ao salvar música");
    }
  };

  const handleDelete = (id, title = "esta música") => {
    setConfirmDialog({
      title: "Excluir música?",
      description: `Esta ação vai remover "${title}" definitivamente do catálogo (de todos os álbuns onde aparece). Não pode ser desfeito.`,
      confirmLabel: "Excluir",
      danger: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/musics/${id}`, { headers: headers() });
          toast.success("Música excluída do catálogo");
          reload();
        } catch {
          toast.error("Erro ao excluir");
        }
      },
    });
  };

  const removeFromPlaylist = (playlistId, musicId, title = "esta música") => {
    setConfirmDialog({
      title: "Remover do álbum?",
      description: `"${title}" será removida apenas deste álbum. A música continua no catálogo e em outros álbuns onde aparece.`,
      confirmLabel: "Remover do álbum",
      danger: false,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/playlists/${playlistId}/musics/${musicId}`, { headers: headers() });
          toast.success("Removida do álbum");
          setPlaylists((prev) =>
            prev.map((p) =>
              p.id !== playlistId
                ? p
                : { ...p, musics: (p.musics || []).filter((m) => m.id !== musicId), music_count: Math.max(0, (p.music_count || 1) - 1) }
            )
          );
        } catch {
          toast.error("Erro ao remover");
        }
      },
    });
  };

  const togglePlay = async (music) => {
    // If clicking the same one that is playing → pause
    if (playingId === music.id) {
      if (audioRef.current) audioRef.current.pause();
      setPlayingId(null);
      return;
    }
    // Otherwise stop current and play new
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
    }
    // Use preview_url directly if present, else fallback to proxy endpoint
    const url = music.preview_url || `${API}/musics/${music.id}/download`;
    if (!url) {
      toast.error("Prévia indisponível para esta faixa");
      return;
    }
    try {
      const audio = new Audio(url);
      audio.preload = "auto";
      audio.addEventListener("ended", () => setPlayingId((cur) => (cur === music.id ? null : cur)));
      audio.addEventListener("error", () => {
        toast.error("Erro ao tocar prévia (URL expirada ou indisponível)");
        setPlayingId(null);
      });
      audioRef.current = audio;
      await audio.play();
      setPlayingId(music.id);
    } catch (err) {
      console.error("play error", err);
      toast.error("Não foi possível iniciar a prévia");
      setPlayingId(null);
    }
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = "";
      }
    };
  }, []);

  const formatPlays = (num) => {
    const n = Number(num) || 0;
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
    return n;
  };

  const filteredMusics = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    if (!q) return musics;
    return musics.filter(
      (m) =>
        m.title.toLowerCase().includes(q) ||
        m.artist_name.toLowerCase().includes(q)
    );
  }, [musics, searchTerm]);

  const filteredPlaylists = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    if (!q) return playlists;
    return playlists
      .map((p) => {
        const matchesPl = (p.name || "").toLowerCase().includes(q);
        const matchedTracks = (p.musics || []).filter(
          (m) =>
            (m.title || "").toLowerCase().includes(q) ||
            (m.artist_name || "").toLowerCase().includes(q)
        );
        if (matchesPl) return p;
        if (matchedTracks.length > 0) return { ...p, musics: matchedTracks };
        return null;
      })
      .filter(Boolean);
  }, [playlists, searchTerm]);

  // Songs not in any album (orphans) - useful overview
  const orphans = useMemo(() => {
    const inAlbums = new Set();
    playlists.forEach((p) => (p.musics || []).forEach((m) => inAlbums.add(m.id)));
    return musics.filter((m) => !inAlbums.has(m.id));
  }, [musics, playlists]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white" data-testid="musics-title">
              Músicas / CDs
            </h1>
            <p className="text-zinc-500 mt-1 text-sm">
              {view === "albums"
                ? "Organizadas por álbum/playlist do artista"
                : "Lista plana de todas as músicas do catálogo"}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* View toggle */}
            <div className="flex bg-[#121212] border border-white/10 rounded-xl p-1 gap-1" data-testid="view-toggle">
              <button
                onClick={() => setView("albums")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  view === "albums" ? "bg-[#FF007F] text-white" : "text-zinc-400 hover:text-white"
                }`}
                data-testid="view-albums"
              >
                <LayoutGrid size={14} /> Por Álbum
              </button>
              <button
                onClick={() => setView("list")}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  view === "list" ? "bg-[#FF007F] text-white" : "text-zinc-400 hover:text-white"
                }`}
                data-testid="view-list"
              >
                <ListIcon size={14} /> Lista Plana
              </button>
            </div>
            <button
              onClick={() => openNewMusic()}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#FF007F] hover:bg-[#FF3399] text-white font-bold rounded-xl transition-all"
              data-testid="add-music-btn"
            >
              <Plus size={18} />
              Nova Música
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
          <input
            type="text"
            placeholder="Buscar álbum, música ou artista..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-12 py-3 bg-[#121212] border border-white/10 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-[#FF007F] transition-colors"
            data-testid="search-musics"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-white/10 rounded-full"
            >
              <XIcon size={16} className="text-zinc-500" />
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-10 h-10 border-2 border-[#FF007F] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : view === "albums" ? (
          <AlbumsView
            playlists={filteredPlaylists}
            orphans={orphans}
            expanded={expanded}
            setExpanded={setExpanded}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            removeFromPlaylist={removeFromPlaylist}
            openNewMusic={openNewMusic}
            formatPlays={formatPlays}
            togglePlay={togglePlay}
            playingId={playingId}
          />
        ) : (
          <FlatListView
            musics={filteredMusics}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            formatPlays={formatPlays}
            togglePlay={togglePlay}
            playingId={playingId}
          />
        )}
      </div>

      {/* Edit/New Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent className="bg-[#121212] border-white/10 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white font-heading text-xl">
              {editingMusic ? "Editar Música" : contextPlaylistId ? "Adicionar Música ao Álbum" : "Nova Música"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Título *</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF007F]"
                placeholder="Nome da música"
                data-testid="music-form-title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-400 mb-2">Artista *</label>
              <input
                type="text"
                value={form.artist_name}
                onChange={(e) => setForm((prev) => ({ ...prev, artist_name: e.target.value }))}
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF007F]"
                placeholder="Nome do artista"
                data-testid="music-form-artist"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">YouTube ID</label>
                <input
                  type="text"
                  value={form.youtube_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, youtube_id: e.target.value }))}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF007F]"
                  placeholder="dQw4w9WgXcQ"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">Duração</label>
                <input
                  type="text"
                  value={form.duration}
                  onChange={(e) => setForm((prev) => ({ ...prev, duration: e.target.value }))}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF007F]"
                  placeholder="3:45"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-zinc-400 mb-2">
                  Plays {editingMusic ? "(total atual)" : "(iniciais)"}
                </label>
                <input
                  type="number"
                  min="0"
                  value={form.plays}
                  onChange={(e) => setForm((prev) => ({ ...prev, plays: parseInt(e.target.value) || 0 }))}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-xl text-white placeholder-zinc-600 focus:outline-none focus:border-[#FF007F] tabular-nums"
                  placeholder="0"
                  data-testid="music-plays-input"
                />
              </div>
              <div className="flex items-center">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.is_release}
                    onChange={(e) => setForm((prev) => ({ ...prev, is_release: e.target.checked }))}
                    className="w-5 h-5 rounded bg-[#0A0A0A] border-white/10 text-[#FF007F] focus:ring-[#FF007F]"
                  />
                  <span className="text-zinc-400">É lançamento</span>
                </label>
              </div>
            </div>

            {contextPlaylistId && !editingMusic && (
              <p className="text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2">
                Esta música será adicionada automaticamente ao álbum selecionado.
              </p>
            )}
          </div>

          <DialogFooter>
            <button
              onClick={() => setDialogOpen(false)}
              className="px-4 py-2 text-zinc-400 hover:text-white transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              className="px-6 py-2 bg-[#FF007F] hover:bg-[#FF3399] text-white font-bold rounded-xl transition-all"
              data-testid="music-form-save"
            >
              {editingMusic ? "Salvar" : "Adicionar"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Dialog (replaces window.confirm which is blocked inside Emergent preview iframe) */}
      <AlertDialog open={!!confirmDialog} onOpenChange={(open) => { if (!open) setConfirmDialog(null); }}>
        <AlertDialogContent className="bg-[#121212] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">{confirmDialog?.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {confirmDialog?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 text-white hover:bg-white/10" data-testid="confirm-cancel">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                const fn = confirmDialog?.onConfirm;
                setConfirmDialog(null);
                if (fn) await fn();
              }}
              className={confirmDialog?.danger ? "bg-red-500 hover:bg-red-600 text-white" : "bg-[#FF007F] hover:bg-[#FF3399] text-white"}
              data-testid="confirm-action"
            >
              {confirmDialog?.confirmLabel || "Confirmar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}

function AlbumsView({
  playlists,
  orphans,
  expanded,
  setExpanded,
  handleEdit,
  handleDelete,
  removeFromPlaylist,
  openNewMusic,
  formatPlays,
  togglePlay,
  playingId,
}) {
  if (playlists.length === 0 && orphans.length === 0) {
    return (
      <div className="bg-[#121212] rounded-2xl border border-white/10 p-16 text-center">
        <Music className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
        <p className="text-zinc-500">Nenhum álbum ou música encontrado</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {playlists.map((pl) => {
        const isOpen = expanded === pl.id;
        return (
          <div
            key={pl.id}
            className="bg-[#121212] rounded-2xl border border-white/10 overflow-hidden"
            data-testid={`album-${pl.id}`}
          >
            <button
              onClick={() => setExpanded(isOpen ? null : pl.id)}
              className="w-full flex items-center gap-4 p-4 hover:bg-white/5 transition-colors text-left"
            >
              {pl.cover_image ? (
                <img src={pl.cover_image} alt="" className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#FF007F]/30 to-[#00E5FF]/30 flex items-center justify-center flex-shrink-0">
                  <Disc className="w-8 h-8 text-white/50" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#FF007F] font-bold uppercase tracking-wider mb-0.5">PLAYLIST</p>
                <h3 className="font-heading text-lg font-bold text-white truncate">{pl.name}</h3>
                <p className="text-zinc-500 text-sm">
                  {pl.music_count ?? (pl.musics?.length || 0)}{" "}
                  {(pl.music_count ?? (pl.musics?.length || 0)) === 1 ? "música" : "músicas"}
                </p>
              </div>
              {isOpen ? (
                <ChevronDown className="text-zinc-500 flex-shrink-0" size={22} />
              ) : (
                <ChevronRight className="text-zinc-500 flex-shrink-0" size={22} />
              )}
            </button>

            {isOpen && (
              <div className="border-t border-white/10">
                {/* Tracks */}
                {(pl.musics || []).length === 0 ? (
                  <p className="text-center text-zinc-600 text-sm py-6">Álbum vazio</p>
                ) : (
                  <div className="divide-y divide-white/5">
                    {(pl.musics || []).map((m, idx) => (
                      <div
                        key={m.id}
                        className="flex items-center gap-3 px-4 py-3 hover:bg-white/5"
                        data-testid={`album-${pl.id}-track-${idx}`}
                      >
                        <span className="w-7 text-center text-zinc-600 tabular-nums text-sm font-bold">
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <button
                          onClick={() => togglePlay(m)}
                          className="w-9 h-9 rounded-full bg-[#FF007F]/15 hover:bg-[#FF007F] text-[#FF007F] hover:text-white flex items-center justify-center transition-all flex-shrink-0 group-hover:scale-105"
                          title={playingId === m.id ? "Pausar" : "Ouvir prévia (30s)"}
                          data-testid={`play-track-${m.id}`}
                        >
                          {playingId === m.id ? <Pause size={14}/> : <Play size={14} className="ml-0.5"/>}
                        </button>
                        {m.artist_image ? (
                          <img src={m.artist_image} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-9 h-9 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                            <Disc size={14} className="text-zinc-600" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-medium truncate">{m.title}</p>
                          <p className="text-zinc-500 text-xs truncate flex items-center gap-1.5">
                            <span className="truncate">{m.artist_name}</span>
                            <span className="opacity-50">•</span>
                            <span className="flex items-center gap-0.5 tabular-nums shrink-0 text-[#FF007F]/80">
                              <TrendingUp size={10} /> {formatPlays(m.plays)}
                            </span>
                          </p>
                        </div>
                        <span className="text-zinc-500 text-xs hidden md:inline tabular-nums">{m.duration || "—"}</span>
                        <button
                          onClick={() => handleEdit(m, pl.id)}
                          className="p-2 hover:bg-amber-500/15 text-amber-400 rounded-lg transition-colors"
                          title="Editar nome / metadados"
                          data-testid={`edit-track-${m.id}`}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => removeFromPlaylist(pl.id, m.id, m.title)}
                          className="p-2 hover:bg-zinc-700/50 text-zinc-400 rounded-lg transition-colors"
                          title="Remover só deste álbum (música continua no catálogo)"
                          data-testid={`unlink-track-${m.id}`}
                        >
                          <ListIcon size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(m.id, m.title)}
                          className="p-2 hover:bg-red-500/15 text-red-500 rounded-lg transition-colors"
                          title="Excluir música do catálogo (definitivo)"
                          data-testid={`delete-track-${m.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add to album */}
                <button
                  onClick={() => openNewMusic(pl.id, pl.musics?.[0]?.artist_name || "")}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-[#FF007F]/10 hover:bg-[#FF007F]/20 text-[#FF007F] text-sm font-bold border-t border-white/5 transition-colors"
                  data-testid={`add-to-album-${pl.id}`}
                >
                  <Plus size={16} /> Adicionar Música a este Álbum
                </button>
              </div>
            )}
          </div>
        );
      })}

      {/* Orphan musics (not linked to any album) */}
      {orphans.length > 0 && (
        <div className="bg-[#0F0F0F] rounded-2xl border border-amber-500/20 overflow-hidden">
          <div className="px-4 py-3 bg-amber-500/10 flex items-center gap-2">
            <Music size={16} className="text-amber-400" />
            <h3 className="font-bold text-amber-400">
              Músicas sem álbum ({orphans.length})
            </h3>
          </div>
          <div className="divide-y divide-white/5">
            {orphans.slice(0, 50).map((m, idx) => (
              <div
                key={m.id}
                className="flex items-center gap-3 px-4 py-3 hover:bg-white/5"
                data-testid={`orphan-${m.id}`}
              >
                <span className="w-7 text-center text-zinc-600 tabular-nums text-sm">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <button
                  onClick={() => togglePlay(m)}
                  className="w-9 h-9 rounded-full bg-[#FF007F]/15 hover:bg-[#FF007F] text-[#FF007F] hover:text-white flex items-center justify-center transition-all flex-shrink-0"
                  title={playingId === m.id ? "Pausar" : "Ouvir prévia (30s)"}
                  data-testid={`play-orphan-${m.id}`}
                >
                  {playingId === m.id ? <Pause size={14}/> : <Play size={14} className="ml-0.5"/>}
                </button>
                {m.artist_image ? (
                  <img src={m.artist_image} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-9 h-9 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                    <Disc size={14} className="text-zinc-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{m.title}</p>
                  <p className="text-zinc-500 text-xs truncate">{m.artist_name}</p>
                </div>
                <button onClick={() => handleEdit(m)} className="p-2 hover:bg-amber-500/15 text-amber-400 rounded-lg">
                  <Edit2 size={14} />
                </button>
                <button onClick={() => handleDelete(m.id, m.title)} className="p-2 hover:bg-red-500/15 text-red-500 rounded-lg">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            {orphans.length > 50 && (
              <p className="text-center text-zinc-500 text-xs py-3">
                +{orphans.length - 50} músicas sem álbum (use "Lista Plana" para ver todas)
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FlatListView({ musics, handleEdit, handleDelete, formatPlays, togglePlay, playingId }) {
  if (musics.length === 0) {
    return (
      <div className="bg-[#121212] rounded-2xl border border-white/10 p-16 text-center">
        <Music className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
        <p className="text-zinc-500">Nenhuma música encontrada</p>
      </div>
    );
  }
  return (
    <div className="bg-[#121212] rounded-2xl border border-white/10 overflow-hidden">
      <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/10 text-zinc-500 text-xs font-bold uppercase tracking-wider">
        <div className="col-span-5">Música</div>
        <div className="col-span-3 hidden sm:block">Artista</div>
        <div className="col-span-1 hidden sm:block">Duração</div>
        <div className="col-span-1 hidden md:block">Plays</div>
        <div className="col-span-2 text-right">Ações</div>
      </div>
      <div className="divide-y divide-white/5">
        {musics.map((m) => (
          <div key={m.id} className="grid grid-cols-12 gap-3 items-center px-4 py-3 hover:bg-white/5" data-testid={`music-row-${m.id}`}>
            <div className="col-span-5 flex items-center gap-3 min-w-0">
              <button
                onClick={() => togglePlay(m)}
                className="w-9 h-9 rounded-full bg-[#FF007F]/15 hover:bg-[#FF007F] text-[#FF007F] hover:text-white flex items-center justify-center transition-all flex-shrink-0"
                title={playingId === m.id ? "Pausar" : "Ouvir prévia (30s)"}
                data-testid={`play-music-${m.id}`}
              >
                {playingId === m.id ? <Pause size={14}/> : <Play size={14} className="ml-0.5"/>}
              </button>
              {m.artist_image ? (
                <img src={m.artist_image} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                  <Disc className="w-5 h-5 text-zinc-600" />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-white text-sm font-medium truncate">{m.title}</p>
                <p className="text-zinc-500 text-xs truncate sm:hidden flex items-center gap-1.5">
                  <span className="truncate">{m.artist_name}</span>
                  <span className="opacity-50">•</span>
                  <span className="flex items-center gap-0.5 text-[#FF007F]/80 tabular-nums shrink-0">
                    <TrendingUp size={10} /> {formatPlays(m.plays)}
                  </span>
                </p>
                {m.youtube_id && (
                  <span className="text-xs text-red-500 flex items-center gap-1">
                    <Youtube size={10} /> YouTube
                  </span>
                )}
              </div>
            </div>
            <div className="col-span-3 hidden sm:block text-zinc-400 text-sm truncate">{m.artist_name}</div>
            <div className="col-span-1 hidden sm:block text-zinc-500 text-sm tabular-nums">{m.duration || "—"}</div>
            <div className="col-span-1 hidden md:flex items-center gap-1 text-zinc-500 text-sm tabular-nums">
              <TrendingUp size={12} /> {formatPlays(m.plays)}
            </div>
            <div className="col-span-2 flex items-center justify-end gap-1">
              <button onClick={() => handleEdit(m)} className="p-2 hover:bg-amber-500/15 text-amber-400 rounded-lg" data-testid={`edit-music-${m.id}`}>
                <Edit2 size={14} />
              </button>
              <button onClick={() => handleDelete(m.id, m.title)} className="p-2 hover:bg-red-500/15 text-red-500 rounded-lg" data-testid={`delete-music-${m.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
